import { getLatestRunOrdinal } from '../AgentSourcedCreatorRow/agentSourcedRowMeta'
import { AgentSourcedRunScope, ISourcingRun } from '../types'

/**
 * The BE statuses meaning "this run's import COMPLETED and its creators are
 * reviewable" — the first clause of the BE's `default_run_ids` below.
 * `SourcingRunStatus` is deliberately a loose `string` (see types.ts) so a new
 * BE status can ship without a FE release, so these are matched as literals,
 * the same way every other status check in this feature does it
 * (`sourcingRunStatusLabel`, `sourcingRunStatusTone`, the `'importing'`/
 * `'failed'` empty-state gates in AgentSourcedCreatorListView). An unknown
 * future status simply isn't preferred and falls through to the non-failed
 * clause — the same tolerant handling as elsewhere.
 */
export const COMPLETED_IMPORT_STATUSES = new Set(['ready_for_vibe_check', 'vibe_check_completed'])

const newestOrdinalMatching = (
  sourcingRuns: ReadonlyArray<ISourcingRun>,
  predicate: (run: ISourcingRun) => boolean,
): number | undefined => {
  const ordinals = sourcingRuns.filter(predicate).map((run) => run.ordinal)
  return ordinals.length > 0 ? Math.max(...ordinals) : undefined
}

/**
 * The default run's ordinal — mirrors the BE's `default_run_ids`
 * (`agent_sourced_creator_queries.rb`), used whenever the `?sourcing_run`
 * param is omitted:
 *
 *   newest_first.find { |r| r.ready_for_vibe_check? || r.vibe_check_completed? } ||
 *   newest_first.find { |r| !r.failed? }
 *
 * BOTH clauses matter (BRAND-9550). The FE previously mirrored only the
 * second one — newest non-failed, full stop — and its doc comment claimed
 * that WAS the whole BE rule; the BE had since gained the preceding
 * completed-import preference, and the stale comment is part of why the drift
 * went unnoticed. `expired` and `importing` are not `failed`, so the FE
 * happily defaulted to a run the BE skips.
 *
 * That divergence used to be cosmetic — a wrong tab label, since both the
 * list AND the bulk reject went through the BE's own default. It stopped
 * being cosmetic once the reject started BINDING the FE's answer (492861e88)
 * while the list still omits the param and gets the BE's: with Run 1
 * `ready_for_vibe_check` and Run 2 `expired`, the list rendered Run 1's
 * pending creators while the reject named ordinal 2, whose scope
 * `bulk_rejectable` (intersected with `decidable`, excluding importing/failed/
 * expired) resolves EMPTY — a 422 `bulk_reject_ids_required` and a dead end
 * for the user. Keeping this in lockstep with the BE is what makes "the run
 * on screen" and "the run in the request" the same run.
 *
 * Final fallback (no BE counterpart, deliberate): when every run has failed
 * both BE clauses come up nil and the BE returns NO run ids at all — an empty
 * list. The FE has to render some tab, so it falls back to the true latest
 * ordinal; its failed record bar is then consistent with the empty list the
 * BE pairs it with. Same reasoning as before BRAND-9550, unchanged.
 */
const getDefaultRunOrdinal = (sourcingRuns: ReadonlyArray<ISourcingRun>): number | undefined =>
  newestOrdinalMatching(sourcingRuns, (run) => COMPLETED_IMPORT_STATUSES.has(run.status)) ??
  newestOrdinalMatching(sourcingRuns, (run) => run.status !== 'failed') ??
  getLatestRunOrdinal(sourcingRuns as ISourcingRun[])

/**
 * Resolves which sourcing run is "the selected one" for a given
 * `selectedRunScope`, shared between RunTabs (the tab strip + record bar)
 * and AgentSourcedCreatorListView (which needs to know the selected run's
 * status to show the BRAND-9307 RUNNING/FAILED empty states).
 *
 * Default-selection contract (BRAND-9515, corrected in BRAND-9550): when no
 * run is explicitly requested — `selectedRunScope` is `null`, `'all'` (no UI
 * entry point since the BRAND-9307 Idea-2 rework), or an ordinal that doesn't
 * match any loaded run — the default is the newest run whose import has
 * COMPLETED (`ready_for_vibe_check`/`vibe_check_completed`), falling back to
 * the newest NON-FAILED run, never unconditionally "the latest run". This
 * matters because the FE sends no `?sourcing_run` param on the initial load,
 * so the BE resolves the default itself and the FE must reach the same
 * answer — see `getDefaultRunOrdinal` above for the BE clauses this mirrors
 * and what broke when it mirrored only half of them. If every run has failed
 * there's nothing to prefer, so this falls back to the true latest run — its
 * failed record bar is then consistent with the BE's empty item list for that
 * same all-failed case.
 *
 * An explicit ordinal that DOES match a loaded run is always honored
 * verbatim regardless of that run's status — clicking directly on a failed
 * run's tab must still show that run's failed/empty state (the BE still
 * honors an explicit `?sourcing_run=<ordinal>` for a failed run).
 *
 * This used to coexist with a second, deliberately different notion of
 * "latest" — the per-row NEW badge, which asked "is this row from the run
 * that just happened" and stayed pinned to the newest run whatever its
 * status. That badge was removed in BRAND-9589, so `getLatestRunOrdinal` is
 * now consumed here only; the distinction is recorded because it is the
 * reason the helper is unconditional rather than status-aware.
 *
 * Tolerates an undefined/absent runs array (not just empty): tests and the
 * token-mode CampaignCreators page preload partial agentSourcedCreators
 * state without a `sourcingRuns` key, and this is called during render.
 */
export const getEffectiveSelectedRun = (
  sourcingRuns: ReadonlyArray<ISourcingRun> | undefined,
  selectedRunScope: AgentSourcedRunScope,
): ISourcingRun | undefined => {
  if (!sourcingRuns || sourcingRuns.length === 0) return undefined

  const requestedOrdinal =
    selectedRunScope === 'all' || selectedRunScope === null ? undefined : selectedRunScope

  const requestedRun =
    requestedOrdinal !== undefined
      ? sourcingRuns.find((run) => run.ordinal === requestedOrdinal)
      : undefined

  if (requestedRun) return requestedRun

  const defaultOrdinal = getDefaultRunOrdinal(sourcingRuns)
  return sourcingRuns.find((run) => run.ordinal === defaultOrdinal)
}
